import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const N8N_WEBHOOK_URL = 'https://finance-manager-adi108.duckdns.org/webhook/automate-ops-lead';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [sectionRef, sectionVisible] = useScrollAnimation();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString(),
          source: 'website_contact_form'
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setError('Failed to submit form. Please try emailing us directly or try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-section" className="contact-section">
      <div className="section-container">
        <div 
          ref={sectionRef}
          className={`contact-grid ${sectionVisible ? 'animate-fade-in' : ''}`}
          style={{ opacity: sectionVisible ? 1 : 0 }}
        >
          <div className="contact-info">
            <h2 className="section-title">Ready to Automate?</h2>
            <p className="contact-description">
              Book your free automation audit and discover how AI agents can transform your business operations.
            </p>
            
            <div className="contact-benefits">
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <span>Free 30-minute consultation</span>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <span>Custom automation roadmap</span>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <span>ROI analysis and projections</span>
              </div>
            </div>
          </div>
          
          <div className="contact-form-wrapper">
            {isSubmitted ? (
              <div className="success-message animate-scale-in">
                <CheckCircle className="success-icon" size={48} />
                <h3 className="success-title">Request Received!</h3>
                <p className="success-text">
                  We'll reach out within 24 hours to schedule your automation audit.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="john@company.com"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Your Company Inc."
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="form-label">What do you want to automate?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Tell us about your automation needs..."
                    rows="4"
                    required
                  />
                </div>
                
                {error && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="btn-primary btn-full-width"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? 'Submitting...' : 'Book Your Automation Audit'}</span>
                  <Send className="btn-icon" size={20} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
